import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, useBreakpointValue} from '@chakra-ui/react'
import {HeroBannerText} from './hero-banner-text'
import {remCalc} from '../../../../../plugins/px-to-rem-babel-custom-plugin/utils/remCalc'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {calculateVerticalPadding, getMediaHeight, getHerobannerGradient} from '../utils'
import useDamImage from '../../../custom-hooks/use-dam-image'

export const HeroBanner = ({
    isMaskHeaderActive = true,
    textBlock,
    variant = 'white', // theme can either be black or white
    height = 'big',
    maxWidth = remCalc(1920),
    hasPaddings = true,
    buttonText,
    countdown,
    ...props
}) => {
    const imageSetKey = `image_${height}`
    const imageSetObj = props?.[imageSetKey]
    const isSmallHeight = height === 'small'
    const isMediumHeight = height === 'mid'

    const damImage = useDamImage(imageSetObj)

    const direction =
        textBlock?.tabletMobileAlignment === 'bottom-center' ? 'column' : 'column-reverse'
    const verticalPadding = calculateVerticalPadding(direction, hasPaddings)

    // const validImageContentTypes = ['image/jpeg', 'image/png', 'image/webp']

    // const isImage = media && media?.contentType?.startsWith('image/')
    const isVideo = props?.videos?.desktop && props?.videos?.mobile

    const imageSrc = damImage.url

    const gradient = getHerobannerGradient(
        textBlock.desktopAlignment,
        props?.maskgroup?.opacity,
        isMaskHeaderActive,
    )

    const mediaHeight = getMediaHeight(height)
    const isHeightAuto = mediaHeight === 'auto'

    const videoSource = useBreakpointValue({
        base: props?.videos?.mobile,
        sm: props?.videos?.desktop,
    })

    return (
        <Flex
            data-testid={`hero-banner-container-${direction}`}
            flexDir={direction}
            pb={verticalPadding}
            maxW={maxWidth}
            backgroundColor={variant === 'black' ? 'background.dark' : 'background.default'}
            w="100%"
            m="0 auto"
        >
            <Flex
                data-testid={
                    isVideo
                        ? 'hero-banner-video'
                        : imageSrc
                        ? 'hero-banner-image'
                        : 'hero-banner-text'
                }
                bgImage={
                    !isVideo
                        ? {
                              base: `url(${imageSrc})`,
                              lg: gradient ? `${gradient}, url(${imageSrc})` : `url(${imageSrc})`,
                          }
                        : undefined
                }
                bgSize={imageSrc && !isVideo ? 'cover' : undefined}
                bgPos={imageSrc && !isVideo ? 'center' : undefined}
                bgRepeat={imageSrc && !isVideo ? 'no-repeat' : undefined}
                pt={isHeightAuto ? {base: '86%', md: '35%', lg: '10%'} : undefined}
                pb={isHeightAuto ? {lg: '10%'} : undefined}
                w="100%"
                h={isVideo ? {base: remCalc(320), md: remCalc(256), lg: mediaHeight} : undefined}
                aspectRatio={damImage?.ratio}
                maxW={maxWidth}
                position="relative"
            >
                {isVideo && (
                    <Box
                        position="absolute"
                        width="100%"
                        height="100%"
                        _after={{
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: {lg: gradient ? `${gradient}` : undefined},
                            pointerEvents: 'none',
                        }}
                    >
                        <video
                            key={videoSource}
                            autoPlay
                            loop
                            muted
                            style={{objectFit: 'cover', height: '100%', width: '100%'}}
                        >
                            <source src={videoSource} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </Box>
                )}
                <HideOnMobile display={{base: 'none', md: 'none', lg: 'flex'}} width="100%">
                    <HeroBannerText
                        variant={variant}
                        countdown={countdown}
                        textBlock={textBlock}
                        buttonText={buttonText}
                        isSmallHeight={isSmallHeight}
                        isMediumHeight={isMediumHeight}
                    />
                </HideOnMobile>
            </Flex>
            <HideOnDesktop display={{base: 'flex', md: 'flex', lg: 'none'}}>
                <HeroBannerText
                    variant={variant}
                    maxWidth={maxWidth}
                    countdown={countdown}
                    textBlock={textBlock}
                    hasPaddings={hasPaddings}
                    buttonText={buttonText}
                />
            </HideOnDesktop>
        </Flex>
    )
}

HeroBanner.propTypes = {
    isMaskHeaderActive: PropTypes.bool,
    textBlock: PropTypes.shape({
        label: PropTypes.string,
        title: PropTypes.string,
        bodyCopy: PropTypes.string,
        desktopAlignment: PropTypes.string,
        tabletMobileAlignment: PropTypes.string,
    }),
    buttonText: PropTypes.object,
    variant: PropTypes.string,
    height: PropTypes.string,
    link: PropTypes.string,
    maxWidth: PropTypes.string,
    hasPaddings: PropTypes.bool,
    countdown: PropTypes.array,
    maskgroup: PropTypes.shape({
        opacity: PropTypes.string,
    }),
    videos: PropTypes.shape({
        desktop: PropTypes.string,
        mobile: PropTypes.string,
    }),
}
